// Coiffures et couvre-chefs. Toutes les coiffures partent du même contour de
// crâne (OUTER), de tempe à tempe, pour qu'aucune ne laisse la peau à nu sur
// les côtés ; les pattes finissent en pointe le long de l'oreille. Seule la
// ligne du front (les FRINGE_*) change d'un style à l'autre.

const INK = "#2B2540";
const STRAW = "#EBCB73";
const STRAW_SHADE = "#D2AC4E";
const STEEL = "#C9CED6";

const OUTER = "M32 57C27 31 42 17 60 17s33 14 28 40";
const OUTER_WIDE = "M27 58C25 31 41 15 60 15s35 16 33 43";
const FRINGE_SHORT = "L84.5 52C84 45 80 39 74 37Q60 41 46 37C40 39 36 45 35.5 52z";
const FRINGE_SWEPT = "L84.5 52C84 46 82 40 77 36C66 39 52 36 43 28C41 38 37 44 35.5 52z";
const FRINGE_BACK = "L85 52C84.5 42 76 33.5 60 33.5S35.5 42 35 52z";
const FRINGE_PARTED = "L84.5 54C84 44 76 34 60 30C44 34 36 44 35.5 54z";
const BUZZ = "M32 56C29 31 43 20.5 60 20.5S91 31 88 56L85.5 52C85 44 80 36 72 33.5Q60 31 48 33.5C40 36 35 44 34.5 52z";

// Le volume de chaque coiffure, pour y ajuster les couvre-chefs (HATS). « wide » : elle déborde
// du crâne (OUTER_WIDE, masse à l'arrière) ; « snug » : elle le suit au plus près ; « sides » :
// des cheveux sur les côtés seulement, le dessus est nu ; « bare » : crâne nu. Les autres s'en
// tiennent à OUTER.
export const HAIR_VOLUMES = {
    bald: "bare",
    balding: "sides",
    buzz: "snug", fade: "snug", cornrows: "snug", mohawk: "snug",
    shoulder: "wide", long: "wide", longside: "wide", wavy: "wide", bob: "wide", braids: "wide", dreads: "wide", afro: "wide", curly: "wide",
};

// Comment chaque couvre-chef se pose sur la tête. Tous sont dessinés pour une coiffure courte,
// puis le dessin grandit ou rétrécit d'un bloc autour du point (60, line) selon le volume de la
// coiffure : `wide`, `snug`, `sides` et `bare` donnent sa taille (1 quand rien n'est dit). Un
// bonnet prend ainsi une taille de plus sur des cheveux longs, un bandeau fait le tour d'un crâne nu.
// - `line` : la hauteur où il s'arrête sur les tempes ;
// - `covers` : il cache le dessus des cheveux, qui ne reparaissent que sous son bord ;
// - `edge` : ce bord, de la tempe gauche à la droite. Droit s'il n'est pas donné, sinon courbé
//   comme le bas du couvre-chef, pour que la frange se voie dessous.
export const HATS = {
    beanie: { line: 44, covers: true, edge: "q28-7.5 56 0", wide: 1.13, snug: 0.96, bare: 0.92 },
    cap: { line: 44, covers: true, wide: 1.09, snug: 0.97, bare: 0.93 },
    bucket: { line: 43.5, covers: true, edge: "q28-4 56 0", wide: 1.06, bare: 0.95 },
    strawhat: { line: 37, covers: true },
    tophat: { line: 32, covers: true },
    beret: { line: 41, covers: true, edge: "q28-7.5 56 0", wide: 1.13, snug: 0.95, bare: 0.9 },
    gradcap: { line: 37, covers: true, edge: "q28-8.5 56 0", wide: 1.13, snug: 0.94, bare: 0.87 },
    bandana: { line: 43, covers: true, edge: "q28-8.5 56 0", wide: 1.13, snug: 0.95, bare: 0.9 },
    headband: { line: 44, wide: 1.13, snug: 0.95, bare: 0.9 },
    plate: { line: 46, wide: 1.13, snug: 0.95, bare: 0.9 },
    catears: { line: 51, wide: 1.1 },
    headphones: { line: 58, wide: 1.08, snug: 0.93, sides: 0.93, bare: 0.9 },
};

// Traits de mèches : `light` les dessine en clair, pour les masses sombres de l'arrière.
function Strands({ d, color, opacity = 0.5, light = false }) {
    return (
        <path d={d} fill="none" stroke={light ? color.fill : color.shade} strokeWidth="1.6" strokeLinecap="round" opacity={opacity} />
    );
}

// Cheveux longs : deux longueurs tombent devant les épaules jusqu'au bas du cadre, le milieu
// s'arrête derrière le cou. Avant, tout s'arrêtait net à hauteur du col et semblait couper le cou.
const LONG_BACK = "M26 54C24 28 40 13 60 13s36 15 34 41l3 74H82C81.5 114 80 103 76 97H44C40 103 38.5 114 38 128H23z";

// Sous un couvre-chef qui cache le dessus des cheveux, une coiffure tout en volume ne garde que
// deux touffes rondes, qui ressortent juste sous son bord (`line`). Coupée telle quelle par le
// couvre-chef, sa masse laissait deux pointes de chaque côté de la tête.
function Puffs({ line, color, big = false }) {
    const [r, r2] = big ? [9, 6.5] : [7, 5.5];
    return (
        <g>
            <g fill={color.fill}>
                <circle cx={37 - r} cy={line + r + 0.5} r={r} />
                <circle cx={83 + r} cy={line + r + 0.5} r={r} />
                <circle cx={37.5 - r2} cy={line + r * 2 - 0.5} r={r2} />
                <circle cx={82.5 + r2} cy={line + r * 2 - 0.5} r={r2} />
            </g>
            <Strands d={`M${33 - r} ${line + r - 1}q3.5-3.5 7 0M${80 + r} ${line + r - 1}q3.5-3.5 7 0`} color={color} />
        </g>
    );
}

// Ce qui passe derrière la tête : longueurs, volume, queue, tresses.
// `underHat` : la hauteur où s'arrête le couvre-chef qui cache le dessus des cheveux, s'il y en a un.
export function HairBack({ style, color, underHat = null }) {
    if (underHat !== null && (style === "afro" || style === "curly")) return <Puffs line={underHat} color={color} big={style === "afro"} />;
    switch (style) {
        case "long":
            return (
                <g>
                    <path d={LONG_BACK} fill={color.shade} />
                    <Strands d="M30.5 66c-1 14-1 28 0 44M89.5 66c1 14 1 28 0 44" color={color} light />
                </g>
            );
        case "wavy":
            return (
                <g>
                    <path d="M26 54C24 28 40 13 60 13s36 15 34 41c4 8-2 14 2 22s-3 14 1 22c-5 4-10 3-13 0H36c-3 3-8 4-13 0 4-8-3-14 1-22s-2-14 2-22z" fill={color.shade} />
                    <Strands d="M30 66c3 7-2 13 1 20s-1 8 0 12M90 66c-3 7 2 13-1 20s1 8 0 12" color={color} light />
                </g>
            );
        case "shoulder":
            return (
                <g>
                    <path d="M27 54C25 28 41 14 60 14s35 14 33 40l1 34c0 6-6 9-11 6H38c-5 3-11 0-11-6z" fill={color.shade} />
                    <Strands d="M31 64c-1 9-1 17 0 26M89 64c1 9 1 17 0 26" color={color} light />
                </g>
            );
        case "longside":
            return (
                <g>
                    <path d={LONG_BACK} fill={color.shade} />
                    <Strands d="M30.5 66c-1 14-1 28 0 44M89.5 66c1 14 1 28 0 44" color={color} light />
                </g>
            );
        case "balding":
            return <path d="M29.5 58C27.5 46 30.5 36 36.5 31H83.5C89.5 36 92.5 46 90.5 58z" fill={color.shade} />;
        case "boxbraids":
            // Chaque tresse part de sous le crâne et s'écarte en descendant : aucune ne flotte à côté de la tête.
            return (
                <g fill="none" stroke={color.shade} strokeWidth="4.6" strokeLinecap="round" strokeDasharray="3 1.6">
                    <path d="M35.5 37C27.5 43 23.7 52 23.7 62V90" />
                    <path d="M84.5 37C92.5 43 96.3 52 96.3 62V90" />
                    <path d="M37.6 60V104M82.4 60V104M42.3 70V97M77.7 70V97" />
                </g>
            );
        case "dreads":
            // Une masse d'un seul tenant qui part du crâne et finit en locks arrondies, de longueurs
            // inégales : aucune ne flotte à côté de la tête.
            return (
                <g>
                    <path d="M26 54C24 28 40 13 60 13s36 15 34 41V90a2.5 2.5 0 0 1-5 0V84a2.5 2.5 0 0 1-5 0V96a2.5 2.5 0 0 1-5 0V88a2.5 2.5 0 0 1-5 0V93a2.5 2.5 0 0 1-5 0V80H51V93a2.5 2.5 0 0 1-5 0V88a2.5 2.5 0 0 1-5 0V96a2.5 2.5 0 0 1-5 0V84a2.5 2.5 0 0 1-5 0V90a2.5 2.5 0 0 1-5 0z" fill={color.shade} />
                    <Strands d="M31 70v14M36 70v14M41 78v10M46 84v4M89 70v14M84 70v14M79 78v10M74 84v4" color={color} opacity={0.45} light />
                </g>
            );
        case "bob":
            return (
                <g>
                    <path d="M26 52C25 27 41 14 60 14s35 13 34 38l1 26c0 7-7 10-12 6H37c-5 4-12 1-12-6z" fill={color.shade} />
                    <Strands d="M30.5 64c-1 7-1 13 1 18M89.5 64c1 7 1 13-1 18" color={color} light />
                </g>
            );
        case "afro":
            return (
                <g>
                    <circle cx="60" cy="42" r="37" fill={color.fill} />
                    <Strands d="M33 28q4-4 8 0M50 12q4-4 8 0M70 13q4-4 8 0M84 30q4-4 8 0M27 46q4-4 8 0M88 48q4-4 8 0M41 17q4-4 8 0" color={color} />
                </g>
            );
        case "curly":
            return <path d="M28 58c-7-4-7-14-1-18-4-8 2-17 10-17 2-8 12-11 18-6 6-6 17-4 20 3 9-2 17 6 14 15 7 4 7 15 0 19z" fill={color.shade} />;
        case "ponytail":
            return (
                <g>
                    <path d="M80 24C98 22 109 39 104 58c-3 12-7 22-4 34-9-4-13-18-10-32 2-12 1-20-10-24z" fill={color.shade} />
                    <Strands d="M92 36c6 8 6 18 3 28-2 8-3 14-1 20" color={color} light />
                </g>
            );
        case "braids":
            return (
                <g fill={color.shade}>
                    {[30.5, 89.5].map((x) => (
                        <g key={x}>
                            {[65, 74, 83, 92].map((y, index) => (
                                <ellipse key={y} cx={x + (index % 2 ? 1 : -1)} cy={y} rx="5.6" ry="6" />
                            ))}
                            <path d={`M${x - 3} 98h6l-3 8z`} />
                        </g>
                    ))}
                </g>
            );
        default:
            return null;
    }
}

// Ce qui passe devant le visage : la masse du dessus et la ligne du front.
export function HairFront({ style, color }) {
    const fill = color.fill;
    switch (style) {
        case "short":
            return (
                <g>
                    <path d={OUTER + FRINGE_SHORT} fill={fill} />
                    <Strands d="M43 27C50 22 62 21 73 24M38 35c2-4 5-6 8-7" color={color} />
                </g>
            );
        case "side":
            return (
                <g>
                    <path d={OUTER + FRINGE_SWEPT} fill={fill} />
                    <Strands d="M44 28C52 23 64 22 76 26M52 31c8-2 16-1 22 2" color={color} />
                </g>
            );
        case "quiff":
            return (
                <g>
                    <path d="M32 57C27 34 36 21 50 15C64 9 84 14 89 30C91 39 90 48 88 57L84.5 52C84 46 81 40 76 37C66 33 54 34 46 38C40 40 36.5 46 35.5 52z" fill={fill} />
                    <Strands d="M46 27C54 19 68 17 80 23M52 30c8-5 18-5 26-1" color={color} />
                </g>
            );
        case "curtains":
            return (
                <g>
                    <path d={`${OUTER}L84.5 52C85 45 78 35 61.5 31L60 34.5L58.5 31C42 35 35 45 35.5 52z`} fill={fill} />
                    <Strands d="M58 30C52 24 44 24 38 31M62 30C68 24 76 24 82 31" color={color} />
                </g>
            );
        case "fringe":
            return (
                <g>
                    <path d={`${OUTER}L85 52C85 49 85 46 84 43.5Q60 41 36 43.5C35 46 35 49 35 52z`} fill={fill} />
                    <Strands d="M48 31v10.5M60 30v11M72 31v10.5" color={color} opacity={0.4} />
                </g>
            );
        case "pixie":
            // Très courte, mèche longue balayée sur le front.
            return (
                <g>
                    <path d={`${OUTER}L84.5 52C84 47 82 43.5 79 41C68 44.5 54 43.5 45 33C42 39.5 38 45.5 35.5 52z`} fill={fill} />
                    <Strands d="M46 31C54 25 66 24 78 29M50 36c8 3 17 4 25 3" color={color} />
                </g>
            );
        case "fade":
            // Dessus court, côtés dégradés.
            return (
                <g fill={fill}>
                    <path d={BUZZ} opacity="0.4" />
                    <path d="M36.5 41C35 26 46 17 60 17s25 9 23.5 24c-7-4.5-16-3.5-23.5-3.5s-16.5-1-23.5 3.5z" />
                    <Strands d="M45 26C52 22 68 22 75 26" color={color} />
                </g>
            );
        case "dreads":
            // Le dessus tombe de chaque côté en deux locks arrondies, qui couvrent l'oreille.
            return (
                <g>
                    <path d="M26.5 60C24.5 31 41 15 60 15s35.5 16 33.5 45v8a2.25 2.25 0 0 1-4.5 0V62a2.25 2.25 0 0 1-4.5 0V52C84 42 76 33.5 60 33.5S36 42 35.5 52V62a2.25 2.25 0 0 1-4.5 0v6a2.25 2.25 0 0 1-4.5 0z" fill={fill} />
                    <Strands d="M57 18.5C47 20.5 38.5 27 34 38M56.5 25C49.5 26.5 44.5 30.5 41 36.5M63 18.5C73 20.5 81.5 27 86 38M63.5 25C70.5 26.5 75.5 30.5 79 36.5M31 46v16M89 46v16" color={color} opacity={0.6} />
                </g>
            );
        case "balding":
            // Le dessus dégarni : les cheveux font le tour par les côtés, arrondis au-dessus de l'oreille.
            return (
                <g fill={fill}>
                    <path d="M31.5 57C29.5 49 30.5 42 34 36.5C37.5 36 40 38.5 39.5 42C37.5 45.5 36.2 49 35.5 53z" />
                    <path d="M88.5 57C90.5 49 89.5 42 86 36.5C82.5 36 80 38.5 80.5 42C82.5 45.5 83.8 49 84.5 53z" />
                </g>
            );
        case "cornrows":
            // Tresses plaquées : des rangs serrés qui partent du front vers l'arrière.
            return (
                <g>
                    <path d={BUZZ} fill={color.shade} />
                    <g fill="none" stroke={fill} strokeWidth="3.6" strokeLinecap="round" strokeDasharray="2.4 1.5">
                        <path d="M38.5 43C37 35 39.5 29 43.5 25" />
                        <path d="M44.5 37.5C44 31 46 26.5 49.5 22.8" />
                        <path d="M52 34.5C52 29 53.5 25 55.8 21.5" />
                        <path d="M60 33.5V20.8" />
                        <path d="M68 34.5C68 29 66.5 25 64.2 21.5" />
                        <path d="M75.5 37.5C76 31 74 26.5 70.5 22.8" />
                        <path d="M81.5 43C83 35 80.5 29 76.5 25" />
                    </g>
                </g>
            );
        case "boxbraids":
            return (
                <g>
                    <path d={OUTER + FRINGE_BACK} fill={fill} />
                    <Strands d="M57 18.5C47 20.5 38.5 27 34 38M56.5 25C49.5 26.5 44.5 30.5 41 36.5M63 18.5C73 20.5 81.5 27 86 38M63.5 25C70.5 26.5 75.5 30.5 79 36.5" color={color} opacity={0.55} />
                    {/* Deux tresses par côté, devant l'oreille : elles la couvrent en entier, sans laisser de peau entre elles. */}
                    <g fill="none" stroke={fill} strokeWidth="4.6" strokeLinecap="round" strokeDasharray="3 1.6">
                        <path d="M33.5 37C29.5 44 28.3 51 28.3 58V95" />
                        <path d="M86.5 37C90.5 44 91.7 51 91.7 58V95" />
                        <path d="M32.9 48V100M87.1 48V100" />
                    </g>
                </g>
            );
        case "shoulder":
            return (
                <g>
                    <path d={OUTER_WIDE + FRINGE_PARTED} fill={fill} />
                    <Strands d="M58 30C50 26 42 30 37 40M62 30C70 26 78 30 83 40" color={color} />
                </g>
            );
        case "longside":
            return (
                <g>
                    <path d={`${OUTER_WIDE}L84.5 56C84 46 82 40 77 36C66 39 52 36 43 28C41 38 37 44 35.5 56z`} fill={fill} />
                    <Strands d="M44 28C52 23 64 22 76 26M52 31c8-2 16-1 22 2" color={color} />
                </g>
            );
        case "spiky":
            // Cinq mèches courbes qui partent vers le haut, et une frange en pointes.
            return (
                <g>
                    <path d="M32 57C29 47 30 39 33 33Q31 27 27 22Q36 22 40 26Q40 17 37 10Q47 13 51 20Q53 11 54 4Q62 9 64 18Q69 10 76 6Q77 15 75 22Q83 18 91 17Q89 26 85 31C89 39 90 48 88 57L84.5 52C84 47 81 41 77 37.5L71 43L64.5 37L58 43L51 37L45 42C40 43 36.5 48 35.5 52z" fill={fill} />
                    <Strands d="M40 26q2 5 1 9M51 20q2.5 6 2 11M64 18q0 6-2 12M75 22q-1 6-4 9" color={color} />
                </g>
            );
        case "messy":
            // Tignasse courte en bataille : petites touffes tout autour, frange hachée sur le front.
            return (
                <g>
                    <path d="M32 57C29 50 27.5 44 29 38L24.5 36L30 32C31 28 33 25 36 22L34 16.5L40.5 19C44.5 16 49 14.5 54 14L55 8.5L60.5 13.5C65.5 13 70.5 14.5 75 17L80.5 13.5L80.5 20C84.5 23 87 27 89 32L94.5 34L90.5 38C92 44 91 50 88 57L84.5 52C84 47 82 43 79 40L75 46.5L70 39.5L64 46L58 38.5L52 46L47 39.5L42 45.5C38 44.5 36 48 35.5 52z" fill={fill} />
                    <Strands d="M42 22c3 3 4 7 4 11M56 17c2 4 2 8 1 12M70 20c-1 4-3 7-6 10M82 28c-3 2-5 5-6 8" color={color} />
                </g>
            );
        case "blaze":
            // Pics droits qui rayonnent dans tous les sens, mèches pointues sur le front.
            return (
                <g>
                    <path d="M32 57C30 50 30 44 32 39L21.5 38L31 31L23.5 21.5L35 24L33 11.5L44 19L47 6L56 16L64 4.5L69 16L80.5 8L80 21L92.5 19L86 30L97.5 35L88 40C90 45 90 51 88 57L84.5 52C84 46 82 41 78 38L73 47.5L67 38.5L60 47L53 38.5L47 47.5L42 38C38 41 36 46 35.5 52z" fill={fill} />
                    <Strands d="M38 30l5 6M47 22l3 9M60 19v10M72 22l-3 9M82 30l-5 6" color={color} opacity={0.4} />
                </g>
            );
        case "warrior":
            // Grandes mèches épaisses et asymétriques, deux à gauche, une au sommet, deux à droite.
            return (
                <g>
                    <path d="M32 57C30 49 30 43 31 38C24 36 18 31 15 24C22 27 27 27 31 25C28 20 28 14 30 9C34 14 39 16.5 45 17C47 11 52 6 60 3C61 10 65 15 71 18C77 14 85 13 93 15C89 19 87 24 87 29C92 29 98 31 103 36C98 37 93 39 89 42C90 47 90 52 88 57L84.5 52C84 47 82 43 79 40C77 44 74 47 70 49C70 45 69 42 67 39C63 44 58 48 52 49C53 45 52 42 50 39C47 42 43 44 39 44C37 46 36 49 35.5 52z" fill={fill} />
                    <Strands d="M31 25c4 2 8 6 10 11M45 17c3 4 4 9 4 14M71 18c-3 4-5 8-6 13M87 29c-4 1-8 4-10 8" color={color} />
                </g>
            );
        case "flat":
            return (
                <g>
                    <path d={`M32 57C29 46 29.5 34 32 26Q33 16 43 16H77Q87 16 88 26C90.5 34 91 46 88 57${FRINGE_SHORT}`} fill={fill} />
                    <Strands d="M44 17v5M52 16.5v5M60 16.5v5M68 16.5v5M76 17v5" color={color} opacity={0.35} />
                </g>
            );
        case "buzz":
            return <path d={BUZZ} fill={fill} opacity="0.88" />;
        case "mohawk":
            return (
                <g fill={fill}>
                    <path d={BUZZ} opacity="0.42" />
                    <path d="M50 35C47 20 52 9 60 6c8 3 13 14 10 29-3-4-17-4-20 0z" />
                    <Strands d="M56 30c-1-8 0-14 3-19M64 30c1-7 0-13-2-18" color={color} />
                </g>
            );
        case "undercut":
            return (
                <g fill={fill}>
                    <path d={BUZZ} opacity="0.42" />
                    <path d="M35 39C32 24 45 14 62 14s29 9 26 23c-8-6-18-3-28-1-10 2-18 0-25 3z" />
                    <Strands d="M42 28C50 20 66 18 80 24M46 33c8-4 20-6 32-4" color={color} />
                </g>
            );
        case "curly":
            return (
                <g fill={fill}>
                    <path d={OUTER + FRINGE_SHORT} />
                    <circle cx="36" cy="38" r="9" />
                    <circle cx="46" cy="27" r="10" />
                    <circle cx="60" cy="23" r="10" />
                    <circle cx="74" cy="27" r="10" />
                    <circle cx="84" cy="38" r="9" />
                    <circle cx="53" cy="34" r="8" />
                    <circle cx="67" cy="34" r="8" />
                    <Strands d="M41 25q4-4 8 0M56 20q4-4 8 0M70 25q4-4 8 0M32 36q4-4 8 0M80 36q4-4 8 0M50 33q3-3 6 0M64 33q3-3 6 0" color={color} />
                </g>
            );
        case "afro":
            return <path d="M32 56C30 32 44 21 60 21s30 11 28 35L85 52C84.5 44 76 36 60 36S35.5 44 35 52z" fill={fill} />;
        case "bob":
            return (
                <g>
                    <path d={`${OUTER_WIDE}L85 56C85 48 83 43 79 40C70 43 50 43 41 40C37 43 35 48 35 56z`} fill={fill} />
                    <Strands d="M44 27C52 22 68 22 76 27" color={color} />
                </g>
            );
        case "long":
        case "braids":
            return (
                <g>
                    <path d={OUTER_WIDE + FRINGE_PARTED} fill={fill} />
                    <Strands d="M58 30C50 26 42 30 37 40M62 30C70 26 78 30 83 40" color={color} />
                </g>
            );
        case "wavy":
            return (
                <g>
                    <path d={`${OUTER_WIDE}L84.5 56C84 46 82 40 77 36C66 39 52 36 43 28C41 38 37 44 35.5 56z`} fill={fill} />
                    <Strands d="M44 28C52 23 64 22 76 26M52 31c8-2 16-1 22 2" color={color} />
                </g>
            );
        case "bun":
            return (
                <g fill={fill}>
                    <circle cx="60" cy="13" r="10" />
                    <path d={OUTER + FRINGE_BACK} />
                    <Strands d="M54 11q6-5 12 0M46 27c4 2 8 3 14 3s10-1 14-3" color={color} />
                </g>
            );
        case "buns":
            return (
                <g fill={fill}>
                    <circle cx="37" cy="21" r="10" />
                    <circle cx="83" cy="21" r="10" />
                    <path d={OUTER + FRINGE_BACK} />
                    <Strands d="M32 19q5-5 10 0M78 19q5-5 10 0" color={color} />
                </g>
            );
        case "ponytail":
            return (
                <g>
                    <path d={OUTER + FRINGE_BACK} fill={fill} />
                    <Strands d="M44 27C52 22 66 21 78 26" color={color} />
                </g>
            );
        default:
            return null;
    }
}

// `snug` : la tête n'a pas de volume de cheveux, le couvre-chef se pose au ras du crâne.
// `scale` : la taille que le couvre-chef a prise pour s'ajuster à la coiffure (HATS).
export function Headwear({ style, color, snug = false, scale = 1 }) {
    switch (style) {
        case "beanie":
            return (
                <g>
                    <circle cx="60" cy="12" r="5.5" fill={color.shade} />
                    <path d="M30.5 42C30 25 43 14 60 14s30 11 29.5 28z" fill={color.fill} />
                    <path d="M29 37.5Q60 31 91 37.5V45Q60 38.5 29 45z" fill={color.shade} />
                </g>
            );
        case "cap":
            return (
                <g>
                    <path d="M31 41C31 25 44 15 60 15s29 10 29 26z" fill={color.fill} />
                    <path d="M27 41c13-4.5 53-4.5 66 0 1 4-3 6.5-7 5.5-16-3.5-36-3.5-52 0-4 1-8-1.5-7-5.5z" fill={color.shade} />
                    <circle cx="60" cy="15.5" r="2" fill={color.shade} />
                </g>
            );
        case "strawhat":
            // Posé haut sur le crâne : le bord s'arrête au-dessus des sourcils, le ruban prend la couleur des accessoires.
            return (
                <g>
                    <ellipse cx="60" cy="31.5" rx="45" ry="8" fill={STRAW_SHADE} />
                    <ellipse cx="60" cy="29.8" rx="45" ry="7.4" fill={STRAW} />
                    <path d="M21 30.5q39 6 78 0" fill="none" stroke={STRAW_SHADE} strokeWidth="1" strokeLinecap="round" opacity="0.7" />
                    <path d="M38 29C38 14 47 6 60 6s22 8 22 23c-7 2.6-37 2.6-44 0z" fill={STRAW} />
                    <path d="M44 14c4-4 10-6 16-6M41 19c5-5 12-8 19-8" fill="none" stroke={STRAW_SHADE} strokeWidth="1" strokeLinecap="round" opacity="0.6" />
                    <path d="M38.2 23.5Q60 19.5 81.8 23.5c0.2 2 0.2 4 0.2 5.5-7 2.6-37 2.6-44 0 0-1.5 0-3.5 0.2-5.5z" fill={color.fill} />
                    <path d="M38 27.2c7 2.6 37 2.6 44 0" fill="none" stroke={color.shade} strokeWidth="1.2" strokeLinecap="round" />
                </g>
            );
        case "bucket":
            return (
                <g>
                    <path d="M36 35C36 20 46 12 60 12s24 8 24 23z" fill={color.fill} />
                    <path d="M26 45c2-8 6-11 10-11h48c4 0 8 3 10 11-11-4-57-4-68 0z" fill={color.shade} />
                </g>
            );
        case "gradcap":
            // Toque de diplômé : une calotte qui coiffe toute la tête, le plateau au-dessus, et un
            // pompon qui prend la couleur des accessoires. La frange reste visible dessous.
            return (
                <g>
                    <path d="M30.5 38C30.5 23 44 15 60 15s29.5 8 29.5 23Q60 29.5 30.5 38z" fill="#1D1A28" />
                    <path d="M30.5 38Q60 29.5 89.5 38v-3Q60 26.5 30.5 35z" fill="#2E2A3C" />
                    {/* La calotte suit la taille de la tête ; le plateau garde la sienne, pour tenir dans le cadre. */}
                    <g transform={scale === 1 ? undefined : `translate(60 17.5) scale(${1 / scale}) translate(-60 -17.5)`}>
                        <path d="M22 17.5v2.8L60 31.5l38-11.2v-2.8L60 28.5z" fill="#15121D" />
                        <path d="M22 17.5L60 6.5l38 11L60 28.5z" fill="#2E2A3C" />
                        <path d="M30 17.5L60 9l30 8.5" fill="none" stroke="#fff" strokeOpacity="0.08" strokeWidth="1.4" strokeLinecap="round" />
                        <path d="M60 17.5l25.5 4.5v10" fill="none" stroke={color.fill} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                        <circle cx="60" cy="17.5" r="2" fill={color.fill} />
                        <circle cx="85.5" cy="32.5" r="2" fill={color.shade} />
                        <path d="M83.5 33.5h4l0.8 7.5h-5.6z" fill={color.fill} />
                        <path d="M84.8 36v4.5M86.2 36v4.5" stroke={color.shade} strokeWidth="0.7" strokeLinecap="round" />
                    </g>
                </g>
            );
        case "beret":
            // Le bandeau serre la tête ; la calotte déborde tout autour et retombe sur un côté, par-dessus lui.
            return (
                <g>
                    <path d="M61 13.5v-4.5" stroke={color.shade} strokeWidth="2.2" strokeLinecap="round" />
                    <path d="M30 38.5Q60 31 90 38.5V42Q60 34.5 30 42z" fill={color.shade} />
                    <path d="M30.5 39.2C25 36.5 24.5 28 30.5 22C37.5 15 48.5 12.5 61 12.5C78.5 12.5 94.5 18.5 98 29C100 35.5 96 41.5 89 42C86.5 42.2 84.5 41 83 39.3Q60 31.6 30.5 39.2z" fill={color.fill} />
                    <path d="M68 16.5C79 18 88.5 23 92 30.5" fill="none" stroke={color.shade} strokeWidth="1.4" strokeLinecap="round" opacity="0.45" />
                </g>
            );
        case "crown":
            return (
                <g>
                    <path d="M41 26l-3-15 9.5 7.5L54 7l6 11 6-11 6.5 11.5L82 11l-3 15z" fill="#F3C441" />
                    <rect x="41" y="24" width="38" height="4.6" rx="1" fill="#DBA92A" />
                    {[50, 60, 70].map((cx) => (
                        <circle key={cx} cx={cx} cy="26.3" r="1.4" fill={color.fill} />
                    ))}
                </g>
            );
        case "bow":
            // Posé sur la tête, incliné comme elle : deux coques, un nœud et deux pans.
            return (
                <g transform={`translate(${snug ? 75.5 : 78.5} ${snug ? 27.5 : 24}) rotate(24)`}>
                    <path d="M-1.5 1.5L-5.5 10 0 7.5zM1.5 1.5L5.5 10 0 7.5z" fill={color.shade} />
                    <path d="M0 0C-5-7.5-13.5-8.5-15-3.2S-7.5 6.5 0 0zM0 0C5-7.5 13.5-8.5 15-3.2S7.5 6.5 0 0z" fill={color.fill} />
                    <path d="M-4-1.5C-7.5-3-10.5-3-12.5-2M4-1.5C7.5-3 10.5-3 12.5-2" fill="none" stroke={color.shade} strokeWidth="1.2" strokeLinecap="round" />
                    <rect x="-3" y="-3.3" width="6" height="6.6" rx="2.4" fill={color.shade} />
                </g>
            );
        case "catears": {
            // Le serre-tête épouse le crâne (plus près quand il n'y a pas de volume de cheveux),
            // et les oreilles sont plantées dessus, pointes vers le haut.
            const r = snug ? 28.5 : 32;
            const lift = snug ? 0 : -3.5;
            return (
                <g>
                    <path d={`M${60 - r} 51A${r} ${r} 0 0 1 ${60 + r} 51`} fill="none" stroke={color.shade} strokeWidth="3" strokeLinecap="round" />
                    <g transform={`translate(0 ${lift})`}>
                        <path d="M35.5 35.5L31.5 12.5 46.5 25z" fill={color.fill} stroke={color.fill} strokeWidth="2.6" strokeLinejoin="round" />
                        <path d="M84.5 35.5L88.5 12.5 73.5 25z" fill={color.fill} stroke={color.fill} strokeWidth="2.6" strokeLinejoin="round" />
                        <path d="M37 30.5L34.8 18.5 42.5 25z" fill="#F7B6C8" />
                        <path d="M83 30.5L85.2 18.5 77.5 25z" fill="#F7B6C8" />
                    </g>
                </g>
            );
        }
        case "tophat":
            return (
                <g>
                    <rect x="41" y="2" width="38" height="27" rx="3" fill="#2E2A3C" />
                    <rect x="41" y="20" width="38" height="6" fill={color.fill} />
                    <path d="M28 30c0-2 2-3.5 4.5-3.5h55c2.5 0 4.5 1.5 4.5 3.5s-2 3.5-4.5 3.5h-55c-2.5 0-4.5-1.5-4.5-3.5z" fill="#1D1A28" />
                </g>
            );
        case "partyhat":
            return (
                <g>
                    <path d="M60 1L77 31Q60 26 43 31z" fill={color.fill} />
                    <path d="M53.5 12.5l13 3M49.5 19.5l21 4.8" stroke="#fff" strokeOpacity="0.7" strokeWidth="2.2" strokeLinecap="round" />
                    <circle cx="60" cy="2.5" r="3.2" fill="#F3C441" />
                </g>
            );
        case "bandana":
            // Foulard noué sur la tête : le nœud sur le côté, d'où retombent deux pans.
            return (
                <g>
                    <path d="M90 40.6L100.5 43.2 99 47.6 90.6 45z" fill={color.fill} />
                    <path d="M91.7 42.6L98.6 51 94.2 53.4 89.1 44.4z" fill={color.fill} />
                    <path d="M29.8 42C29.8 26 43.5 15.5 60 15.5s30.2 10.5 30.2 26.5Q60 33.5 29.8 42z" fill={color.fill} />
                    <path d="M29.8 39Q60 30.5 90.2 39v5Q60 35.5 29.8 44z" fill={color.shade} />
                    <ellipse cx="90.4" cy="42.3" rx="2.3" ry="2.9" fill={color.shade} />
                </g>
            );
        case "flower":
            // Piquée dans les cheveux, ou posée contre le crâne quand il n'y en a pas.
            return (
                <g transform={`translate(${snug ? -3 : 0} ${snug ? 3.5 : 0})`}>
                    {[0, 72, 144, 216, 288].map((angle) => (
                        <ellipse key={angle} cx="81" cy="22.8" rx="3.3" ry="5" fill={color.fill} transform={`rotate(${angle} 81 28)`} />
                    ))}
                    <circle cx="81" cy="28" r="3" fill="#F3C441" />
                </g>
            );
        case "headband":
            // Il fait le tour de la tête : ses bouts arrivent au bord des cheveux, pas au milieu.
            return <path d="M29.8 40.5Q60 31.5 90.2 40.5V46.5Q60 37.5 29.8 46.5z" fill={color.fill} />;
        case "plate":
            // Bandeau de tissu noué sur le côté, avec sa plaque de métal, sans emblème.
            return (
                <g>
                    <path d="M89.2 44.5l10.5 3-4 3 5 6.5-7.5-2-3.5-4z" fill={color.shade} />
                    <path d="M29.8 42.5Q60 31.5 90.2 42.5V49.5Q60 38.5 29.8 49.5z" fill={color.fill} />
                    <path d="M30.8 48.2Q60 37.6 89.2 48.2" fill="none" stroke={color.shade} strokeWidth="1.2" strokeLinecap="round" />
                    <path d="M45.5 36.2Q60 33 74.5 36.2V44.8Q60 41.6 45.5 44.8z" fill={STEEL} stroke="#9AA1AD" strokeWidth="0.8" strokeLinejoin="round" />
                    <path d="M47.5 37.3Q60 34.6 72.5 37.3" fill="none" stroke="#fff" strokeOpacity="0.7" strokeWidth="1" strokeLinecap="round" />
                    <path d="M53 40.3q7-1.3 14 0" fill="none" stroke="#9AA1AD" strokeWidth="1.2" strokeLinecap="round" />
                    <circle cx="48.5" cy="40.2" r="0.9" fill="#8B92A0" />
                    <circle cx="71.5" cy="40.2" r="0.9" fill="#8B92A0" />
                </g>
            );
        case "headphones":
            return (
                <g>
                    <path d="M29 56C26.5 30 43 14.5 60 14.5s33.5 15.5 31 41.5" fill="none" stroke={INK} strokeWidth="4" strokeLinecap="round" />
                    <rect x="23.5" y="49" width="10.5" height="19" rx="4.8" fill={color.fill} />
                    <rect x="86" y="49" width="10.5" height="19" rx="4.8" fill={color.fill} />
                    <path d="M26.5 54v9M93.5 54v9" stroke={color.shade} strokeWidth="1.8" strokeLinecap="round" />
                </g>
            );
        case "hijab":
            return (
                <g>
                    <path
                        fillRule="evenodd"
                        d="M27 58C25 30 41 14 60 14s35 16 33 44c-1 22 0 40 10 70H17c10-30 11-48 10-70zM37 57c0 15 10 27 23 27s23-12 23-27c0-14-10-22-23-22s-23 8-23 22z"
                        fill={color.fill}
                    />
                    <path d="M37 68c4 15 14 24 23 24s19-9 23-24" fill="none" stroke={color.shade} strokeWidth="2" strokeLinecap="round" />
                </g>
            );
        default:
            return null;
    }
}
